import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import { Home, ShieldCheck, Wifi, MapPin, Users, HeartHandshake, Sparkles, Bed } from 'lucide-react';

const FEATURE_ICONS = [Bed, ShieldCheck, MapPin, Wifi, Users, HeartHandshake, Sparkles, Home];

export const Housing = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = t('housing.features', { returnObjects: true }) || [];

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="housing"
      data-testid="housing-section"
      ref={ref}
      className="py-16 lg:py-20 bg-slate-50"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          data-testid="housing-card"
          className="relative overflow-hidden rounded-3xl shadow-2xl"
          style={{
            background:
              'radial-gradient(120% 90% at 85% 0%, #1b3f66 0%, transparent 60%), #14304f',
          }}
        >
          {/* Filet doré supérieur */}
          <div
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{
              background:
                'linear-gradient(90deg, #b8963e 0%, #d9bd76 45%, transparent 100%)',
            }}
          />

          <div className="relative px-6 sm:px-10 lg:px-16 py-14 lg:py-20 text-ivory">
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-block w-9 h-px bg-amber-400" />
              <span className="text-[11px] tracking-[0.22em] uppercase text-amber-200 font-inter">
                {t('housing.eyebrow')}
              </span>
            </div>

            {/* Titre */}
            <h2 className="font-playfair font-medium text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.08] tracking-tight max-w-3xl">
              {t('housing.title')}
            </h2>
            <p className="font-playfair italic text-amber-200/90 text-lg sm:text-xl lg:text-2xl mt-3 max-w-3xl">
              {t('housing.subtitle')}
            </p>
            <p className="font-inter text-slate-200/90 text-base mt-6 max-w-2xl leading-relaxed">
              {t('housing.intro')}
            </p>

            {/* Grille : liste + rareté */}
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-start">
              {/* Liste des prestations */}
              <ul className="divide-y divide-white/10">
                {features.map((label, i) => {
                  const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
                  return (
                    <motion.li
                      key={label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.45, delay: 0.08 * i }}
                      data-testid={`housing-feature-${i}`}
                      className="flex items-start gap-4 py-3.5"
                    >
                      <span className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full border border-amber-400/60 bg-amber-400/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-amber-300" />
                      </span>
                      <span className="font-inter text-slate-100/95 text-[0.98rem] leading-relaxed">
                        {label}
                      </span>
                    </motion.li>
                  );
                })}
              </ul>

              {/* Bloc rareté */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.25 }}
                data-testid="housing-scarcity"
                className="p-7 lg:p-9 border border-amber-400/40"
                style={{ background: '#0d2138' }}
              >
                <h3 className="font-playfair font-semibold text-2xl text-white mb-6">
                  {t('housing.scarcity.title')}
                </h3>

                {/* 5 arches */}
                <ul className="flex gap-2.5 mb-6 list-none p-0" aria-hidden="true">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <li
                      key={n}
                      className="flex-1 aspect-[3/4] flex items-end justify-center pb-2 border border-amber-400/70 font-playfair text-amber-200 text-lg"
                      style={{
                        borderRadius: '9999px 9999px 3px 3px',
                        background:
                          'linear-gradient(180deg, rgba(184,150,62,.18) 0%, transparent 70%)',
                      }}
                    >
                      {n}
                    </li>
                  ))}
                </ul>

                <p className="font-inter text-sm text-slate-200/90 mb-3 leading-relaxed">
                  <Trans
                    i18nKey="housing.scarcity.legend"
                    components={{ strong: <strong className="text-white font-medium" /> }}
                  />
                </p>
                <p className="font-inter text-[13px] text-slate-400 mb-6 leading-relaxed">
                  {t('housing.scarcity.modalities')}
                </p>

                <button
                  type="button"
                  onClick={scrollToContact}
                  data-testid="housing-cta-btn"
                  className="inline-block px-8 py-3.5 bg-amber-500 hover:bg-transparent text-slate-900 hover:text-amber-200 font-inter font-medium text-[13px] tracking-[0.13em] uppercase border border-amber-500 transition-colors"
                >
                  {t('housing.scarcity.cta')}
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
